import { Fragment, useEffect, useMemo, useState } from 'react';
import { tradeApi } from './api.js';
import AnswerField from './AnswerField.jsx';
import {
  FIELDS, INITIAL, REQUEST_TYPES, MANAGERS, MANAGER_QUESTIONS, MODEL_SERIES, MODEL_EQUITY,
  questionsFor, toggleAccount, accountBlocker, ticketProblems, answersToSend, newTicketKey, money, showAnswer, isTaxable,
  taxLabel, typeLabels, rowQuestionGroups, rowAnswersToSend, setUpsToSend, allocationOf, householdSituation, typesFor,
  accountsNeedingAllocation, managerOf, chosenManager, needsSetUp, managerQuestionShown, managerAnswerPairs,
  defaultGainsBudget, modelParts, composeModel, describeModel,
} from './fields.js';

// A refresh or a dropped connection must never cost the advisor what they
// typed, so the ticket is kept in this tab's session storage until it is sent.
function draftKey(householdId, replacing) {
  return replacing ? `trade-draft:replace:${replacing}` : `trade-draft:${householdId}`;
}
function loadDraft(key) {
  try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; }
}
function saveDraft(key, draft) {
  try { sessionStorage.setItem(key, JSON.stringify(draft)); } catch { /* private mode: keep going without */ }
}
function clearDraft(key) {
  try { sessionStorage.removeItem(key); } catch { /* nothing to clear */ }
}
function shortDate(iso) {
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// How the accounts are introduced on an initial allocation, in Investment Management's own terms.
const INTROS = {
  taxable: 'Investment Management wants the full picture of a taxable account before it trades: transfers still to come, cash the client needs beyond what is on file, money passing through, positions to keep, and this year\'s gains budget.',
  retirement: 'Investment Management invests a retirement account on its first deposit and picks up later deposits itself, grouping IRAs with Roths. It needs your go-ahead, any cash to hold back, and whether it may sell everything.',
  Vise: 'Vise trades the account directly, so it needs the strategy, any buying restrictions, the cash target and the tax budget.',
  PIMCO: 'PIMCO runs the account as a bond separately managed account, so it needs the strategy, the cash target and the tax budget.',
  'All Cash': 'An all-cash account is not invested in a model, so the only decision is how the cash is held.',
  Pontera: 'Pontera trades the account directly, so the model and one holding decision are all it needs.',
  'Other SMA': 'This account is on a separately managed account Salesforce names. Say what Investment Management should know about it.',
};

/** Which group of the per-account section an account belongs to, by who manages it. */
function groupKeyFor(account, given) {
  const m = chosenManager(account, given);
  if (m === 'Pure') return isTaxable(account) ? 'taxable' : 'retirement';
  return m;
}
const GROUP_TITLES = { taxable: 'Taxable accounts', retirement: 'Retirement accounts', Vise: 'Vise accounts', PIMCO: 'PIMCO accounts',
  'All Cash': 'All-cash accounts', Pontera: 'Pontera accounts', 'Other SMA': 'Other separately managed accounts' };
const GROUP_ORDER = ['taxable', 'retirement', 'Vise', 'PIMCO', 'All Cash', 'Pontera', 'Other SMA'];

/** A link into Salesforce for a record the ticket shows, opened in its own tab. */
function SfLink({ base, id, children }) {
  if (!base || !id) return <>{children}</>;
  return <a href={`${base}/${id}`} target="_blank" rel="noopener noreferrer">{children}</a>;
}

/** What Salesforce already holds about an account's cash and systematics, so the advisor adds to it rather than repeating it. */
function OnFile({ account, sfBase }) {
  const reserves = account.cashReserves || [];
  const cases = account.openCases || [];
  const items = [];
  for (const r of reserves) {
    items.push(<li key={`r-${items.length}`}>Cash reserve on file: <strong>{money(r.amount)}</strong>{r.category ? ` · ${r.category}` : ''}{r.recurring ? ' · recurring' : ''}{r.endDate ? ` · until ${shortDate(r.endDate)}` : ''}{r.note ? ` · ${r.note}` : ''}</li>);
  }
  for (const c of cases) {
    items.push(
      <li key={`c-${c.id}`}>
        {c.systematic ? `Systematic (${c.systematic.toLowerCase()})` : c.type || c.recordType || 'Case'}{c.amount ? ` · ${money(c.amount)}` : c.cashAmount ? ` · ${money(c.cashAmount)}` : ''}:{' '}
        <SfLink base={sfBase} id={c.id}>{c.number}</SfLink> {c.subject} <span className="tt-tag">{c.status}</span>
      </li>,
    );
  }
  for (const d of account.activeAverages || []) {
    items.push(<li key={`d-${d.name}`}>Active dollar-cost average {d.name} · {money(d.amount)}{d.frequency ? ` ${d.frequency.toLowerCase()}` : ''}</li>);
  }
  for (const p of account.excludedPositions || []) {
    items.push(<li key={`p-${p.ticker}`}>Excluded on file: {p.ticker}{p.name ? ` ${p.name}` : ''}{p.reason ? ` · ${p.reason}` : ''}</li>);
  }
  if (!items.length) return <p className="tt-onfile tt-muted">Nothing on file for this account: no cash reserves, systematics or excluded positions.</p>;
  return <ul className="tt-onfile">{items}</ul>;
}

/** The questions that belong to the account's manager (Vise, PIMCO, All Cash, Pontera). */
function ManagerQuestions({ manager, answers, onChange, idSuffix }) {
  const qs = MANAGER_QUESTIONS[manager] || [];
  if (!qs.length) return null;
  return (
    <div className="tt-grid">
      {qs.filter(q => managerQuestionShown(q, answers)).map(q => {
        const id = `tt-mq-${q.key}-${idSuffix}`;
        const v = answers[q.key] ?? q.default ?? '';
        if (q.kind === 'check') {
          return (
            <label key={q.key} className="tt-field is-check" htmlFor={id}>
              <input id={id} type="checkbox" checked={Boolean(answers[q.key])} onChange={e => onChange(q.key, e.target.checked)} />
              <span>{q.label}</span>
            </label>
          );
        }
        let input;
        if (q.kind === 'choice') {
          input = (
            <div className="tt-radios" role="radiogroup" aria-labelledby={`${id}-label`}>
              {q.options.map(o => (
                <label key={o} className={'tt-radio' + (v === o ? ' is-on' : '')}>
                  <input type="radio" name={id} checked={v === o} onChange={() => onChange(q.key, o)} />{o}
                </label>
              ))}
            </div>
          );
        } else {
          const numeric = q.kind === 'currency' || q.kind === 'percent';
          input = (
            <div className={'tt-input' + (q.kind === 'currency' ? ' has-prefix' : '') + (q.kind === 'percent' ? ' has-suffix' : '')}>
              {q.kind === 'currency' && <span className="tt-affix">$</span>}
              <input id={id} type="text" inputMode={numeric ? 'decimal' : undefined} value={v}
                onChange={e => onChange(q.key, numeric ? e.target.value.replace(/[^0-9.,-]/g, '') : e.target.value)} />
              {q.kind === 'percent' && <span className="tt-affix">%</span>}
            </div>
          );
        }
        return (
          <div key={q.key} className={'tt-field' + (q.kind === 'choice' ? ' is-wide' : '')}>
            <label id={`${id}-label`} htmlFor={id}>{q.label}</label>
            {input}
            {q.help && <p className="tt-help">{q.help}</p>}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The model a model change moves to, chosen the way Investment Management thinks of it: the series and the
 * equity allocation, with the Plus sleeve from the sleeves and modifications. The guideline on file is shown
 * for comparison; the code Salesforce stores is composed from the choice.
 */
function ModelPicker({ value, onChange, chosen, modifications, invalid }) {
  const parts = modelParts(value) || {};
  const plus = String(modifications || '').split(';').includes('Plus Sleeve');
  const current = [...new Set(chosen.map(a => a.ipsModel).filter(Boolean))];
  const tamarac = [...new Set(chosen.map(a => a.tamaracModel).filter(Boolean))];
  const set = (series, equity) => onChange('Requested_Model__c', composeModel(series, equity, plus));
  useEffect(() => {
    // The Plus sleeve is part of the model code, so the chips and the code stay in step.
    if (parts.series && parts.plus !== plus) onChange('Requested_Model__c', composeModel(parts.series, parts.equity, plus));
  }, [plus]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={'tt-field is-wide tt-model' + (invalid ? ' is-invalid' : '')}>
      <label>Model to move to</label>
      <p className="tt-help">
        Guideline IPS on file: <strong>{current.join(', ') || 'none'}</strong>{current.length === 1 && modelParts(current[0]) ? ` (${describeModel(current[0])})` : ''}
        {tamarac.length > 0 && <> · Tamarac model {tamarac.join(', ')}</>}
      </p>
      <div className="tt-grid">
        <div className="tt-field">
          <label htmlFor="tt-model-equity">Equity allocation</label>
          <select id="tt-model-equity" value={parts.equity || ''} onChange={e => set(parts.series || '400', e.target.value)}>
            <option value="">Choose…</option>
            {MODEL_EQUITY.map(e => <option key={e} value={e}>{e}%</option>)}
          </select>
        </div>
        <div className="tt-field">
          <label htmlFor="tt-model-series">Series</label>
          <select id="tt-model-series" value={parts.series || ''} onChange={e => set(e.target.value, parts.equity || 60)}>
            <option value="">Choose…</option>
            {MODEL_SERIES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <p className="tt-help">{value ? <>Moves to <strong>{value}</strong>: {describeModel(value)}.</> : 'Choose the equity allocation and the series.'} {FIELDS.Requested_Model__c.help}</p>
    </div>
  );
}

/**
 * A new trade request for one household, or the replacement for a request
 * Investment Management sent back (`replacing` is that request, already loaded).
 */
export default function NewTicket({ householdId, replacing, onHousehold, navigate }) {
  const storeKey = draftKey(householdId, replacing && replacing.id);
  const [ctx, setCtx] = useState({ state: 'loading' });
  const [draft, setDraft] = useState(() => {
    const kept = loadDraft(storeKey);
    if (kept) return { override: false, ...kept };
    if (replacing) {
      return {
        key: newTicketKey(),
        selected: replacing.accounts.map(a => a.id),
        types: replacing.types,
        answers: { ...replacing.answers },
        casesReviewed: Boolean(replacing.casesReviewed),
        consideredTax: Boolean(replacing.consideredTax),
        accountAnswers: Object.fromEntries(replacing.accounts.map(a => [a.id, { ...(a.answers || {}) }])),
        message: '',
        override: true,
      };
    }
    return { key: newTicketKey(), selected: [], types: [], answers: {}, accountAnswers: {}, casesReviewed: false, consideredTax: false, message: '', override: false };
  });
  const [view, setView] = useState('edit'); // edit | review | sent
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState(null); // { error, field }
  const [averages, setAverages] = useState(null);
  const [sent, setSent] = useState(null);
  const [showProblems, setShowProblems] = useState(false);

  useEffect(() => { if (view !== 'sent') saveDraft(storeKey, draft); }, [draft, storeKey, view]);

  useEffect(() => {
    let live = true;
    tradeApi.household(householdId).then(res => {
      if (!live) return;
      if (!res.ok) { setCtx({ state: 'failed', error: res.body.error, status: res.status }); return; }
      setCtx({ state: 'ready', ...res.body });
      if (onHousehold) onHousehold(res.body.household.name);
      // A household with accounts that have never been allocated opens straight into their initial allocation,
      // with those accounts chosen. Only an untouched ticket: a draft the advisor has started keeps what they chose.
      if (!replacing && householdSituation(res.body.accounts) !== 'allocated') {
        const fresh = accountsNeedingAllocation(res.body.accounts);
        setDraft(d => (d.types.length || d.selected.length ? d : { ...d, types: [INITIAL], selected: fresh }));
      }
    });
    return () => { live = false; };
  }, [householdId, onHousehold]);

  const selected = useMemo(() => new Set(draft.selected), [draft.selected]);
  const update = patch => setDraft(d => ({ ...d, ...patch }));
  const setAnswer = (field, value) => setDraft(d => ({ ...d, answers: { ...d.answers, [field]: value } }));
  const setRowAnswer = (accountId, field, value) => setDraft(d => ({
    ...d, accountAnswers: { ...(d.accountAnswers || {}), [accountId]: { ...((d.accountAnswers || {})[accountId] || {}), [field]: value } },
  }));
  const setManagerAnswer = (accountId, key, value) => setDraft(d => {
    const row = (d.accountAnswers || {})[accountId] || {};
    return { ...d, accountAnswers: { ...(d.accountAnswers || {}), [accountId]: { ...row, manager: { ...(row.manager || {}), [key]: value } } } };
  });

  if (ctx.state === 'loading') {
    return <div className="tt-loading"><div className="load-spinner" /><p>Reading the household from Salesforce…</p></div>;
  }
  if (ctx.state === 'failed') {
    return (
      <div className="tt-page">
        <div className="tt-banner is-error" role="alert">
          <strong>The household could not be opened.</strong> {ctx.error}
        </div>
      </div>
    );
  }

  const { household, accounts, openCases, openRequests, salesforceUrl } = ctx;
  // Municipal Bonds is the older value; the ticket offers national and CA munis instead.
  const picklists = { ...ctx.picklists, Model_Modifications__c: (ctx.picklists.Model_Modifications__c || []).filter(o => o.value !== 'Municipal Bonds') };
  const chosen = accounts.filter(a => selected.has(a.id));
  const questions = questionsFor(draft.types, chosen);
  const problems = ticketProblems({
    household: { ...household, openCaseCount: openCases.length },
    accounts, selected, types: draft.types, answers: draft.answers,
    casesReviewed: draft.casesReviewed, consideredTax: draft.consideredTax, accountAnswers: draft.accountAnswers || {},
  });
  const hard = problems.filter(p => !p.soft);
  const soft = problems.filter(p => p.soft);
  const type = draft.types[0] || null;
  const initial = type === INITIAL;
  // What the household's situation allows: a new household is an initial allocation and nothing else is
  // offered; a household with new accounts assumes one; an allocated household is not offered one. The advisor
  // can always ask for the full list.
  const situation = householdSituation(accounts);
  const needing = accounts.filter(a => allocationOf(a) === 'needs');
  const showTypes = situation === 'allocated' || draft.override || (type && !initial);
  const heldOut = initial ? chosen.filter(a => needsSetUp(a, (draft.accountAnswers || {})[a.id])) : [];
  const going = chosen.filter(a => !heldOut.includes(a));
  // A request has one type. Accounts that do not fit the new type drop out, and for the other types the
  // selection narrows to one Tamarac group, or one account, as Investment Management asks.
  function chooseType(value) {
    let next = new Set(draft.selected);
    for (const a of chosen) if (accountBlocker(a, value)) next.delete(a.id);
    if (value !== INITIAL) {
      const first = accounts.find(a => next.has(a.id));
      next = first ? toggleAccount(new Set(), first, accounts, true, true) : new Set();
      for (const a of accounts) if (next.has(a.id) && accountBlocker(a, value)) next.delete(a.id);
    } else if (next.size === 0) {
      next = new Set(accountsNeedingAllocation(accounts));
    }
    update({ types: [value], selected: [...next] });
  }
  const invalid = new Set(showProblems ? hard.map(p => p.field) : []);
  if (refusal && refusal.field) invalid.add(refusal.field);
  // Investment Management's types that fit the situation, offered only where Salesforce still accepts the value.
  const allowed = new Set((picklists.Allocation_Type_Multi__c || []).map(o => o.value));
  const typeOptions = typesFor(situation, draft.override).filter(o => allowed.has(o.value));
  const typeHint = type ? (REQUEST_TYPES.find(o => o.value === type) || {}).hint : null;
  const others = openRequests.filter(r => !replacing || r.id !== replacing.id);

  function ticketBody(cancelActiveAverages) {
    return {
      householdId,
      clientRequestId: draft.key,
      accountIds: going.map(a => a.id),
      types: draft.types,
      ...(initial ? { accountAnswers: rowAnswersToSend(draft.accountAnswers, going), setupCases: setUpsToSend(draft.accountAnswers, heldOut) } : {}),
      casesReviewed: draft.casesReviewed,
      consideredTax: chosen.some(isTaxable) ? draft.consideredTax : false,
      cancelActiveAverages: Boolean(cancelActiveAverages),
      fields: answersToSend(draft.answers, draft.types, chosen),
      ...(replacing ? { message: draft.message } : {}),
    };
  }

  async function submit(cancelActiveAverages) {
    setSending(true);
    setRefusal(null);
    const res = replacing
      ? await tradeApi.replace(replacing.id, ticketBody(cancelActiveAverages))
      : await tradeApi.submit(ticketBody(cancelActiveAverages));
    setSending(false);
    if (res.ok) {
      clearDraft(storeKey);
      setAverages(null);
      setSent(res.body);
      setView('sent');
      return;
    }
    if (res.status === 409 && res.body.needsAverageCancellation) {
      setAverages(res.body.averages || []);
      return;
    }
    setRefusal({ error: res.body.error || 'Salesforce refused the request without saying why.', field: res.body.field });
  }

  // The accounts' answers as they will read, for the review and the per-account cards.
  const saidFor = a => Object.entries(rowAnswersToSend(draft.accountAnswers, [a])[a.id] || {})
    .filter(([f]) => f !== 'kindAnswers' && f !== 'Managed_By__c')
    .map(([f, v]) => `${FIELDS[f].label}: ${showAnswer(f, v)}`);
  const pairsFor = a => managerAnswerPairs(chosenManager(a, (draft.accountAnswers || {})[a.id]), ((draft.accountAnswers || {})[a.id] || {}).manager);

  if (view === 'sent') {
    const cases = sent.setupCases || [];
    return (
      <div className="tt-page">
        <section className="tt-card tt-sent">
          <div className="tt-sent-mark" aria-hidden="true">✓</div>
          <h2>{sent.duplicate ? 'This request was already sent' : sent.id ? 'Sent to Investment Management' : 'Operations cases opened'}</h2>
          {sent.id ? (
            <p>
              {household.name} · <strong>{sent.name}</strong> · {sent.status}
              {replacing && <> · replaces {replacing.name}, which is now cancelled</>}
              {sent.case && <> · linked to model change case {sent.case.number}</>}
            </p>
          ) : (
            <p>{household.name}: nothing went to Investment Management yet. Once Operations finishes the set-up, send the initial allocation for those accounts.</p>
          )}
          {cases.length > 0 && (
            <ul className="tt-list tt-left">
              {cases.map(c => (
                <li key={c.id}>Operations case <SfLink base={salesforceUrl} id={c.id}>{c.number}</SfLink> · {c.re || c.subject} <span className="tt-tag">{c.status}</span></li>
              ))}
            </ul>
          )}
          {sent.id && (
            <p className="tt-muted">
              It is in Investment Management's queue in Salesforce now. If they need anything, it comes back to you here and in Salesforce's notifications.
            </p>
          )}
          <div className="tt-actions">
            {sent.id && <button className="btn btn-primary" onClick={() => navigate(`/trade/requests/${sent.id}`)}>View the request</button>}
            <button className="btn btn-light" onClick={() => navigate('/trade/requests')}>My requests</button>
          </div>
        </section>
      </div>
    );
  }

  if (view === 'review') {
    return (
      <div className="tt-page">
        <header className="tt-page-head">
          <div>
            <p className="tt-kicker">{replacing ? `Replacing ${replacing.name}` : 'New trade request'} · review</p>
            <h1>{household.name}</h1>
          </div>
        </header>
        {refusal && (
          <div className="tt-banner is-error" role="alert">
            <strong>Salesforce did not accept this request.</strong> {refusal.error}
            <div className="tt-banner-actions"><button className="btn btn-light btn-sm" onClick={() => setView('edit')}>Back to edit</button></div>
          </div>
        )}
        {averages && (
          <div className="tt-banner is-warn" role="alert">
            <strong>These accounts have an active dollar-cost average.</strong> Sending this request cancels it and its scheduled purchases, exactly as the wizard does.
            <ul>{averages.map(a => <li key={a.id}>{a.name} · {money(a.amount)}{a.startDate ? ` · started ${a.startDate}` : ''}</li>)}</ul>
            <div className="tt-banner-actions">
              <button className="btn btn-primary btn-sm" disabled={sending} onClick={() => submit(true)}>Cancel the average and send</button>
              <button className="btn btn-light btn-sm" onClick={() => { setAverages(null); setView('edit'); }}>Back to edit</button>
            </div>
          </div>
        )}
        {soft.map((p, i) => <div key={i} className="tt-banner is-warn">{p.message}</div>)}
        <section className="tt-card">
          <h2>What Investment Management will receive</h2>
          <dl className="tt-summary">
            <dt>Request</dt><dd>{typeLabels(draft.types)}</dd>
            <dt>Accounts</dt>
            <dd>
              <ul className="tt-plain">
                {going.map(a => {
                  const given = (draft.accountAnswers || {})[a.id];
                  const manager = chosenManager(a, given);
                  const said = initial ? saidFor(a) : [];
                  const pairs = initial ? pairsFor(a) : [];
                  return (
                    <li key={a.id}>
                      {a.name} · {a.type} · {taxLabel(a) ? `${taxLabel(a).toLowerCase()} · ` : ''}{money(a.value)} · IPS {a.ipsModel} · risk {a.riskProfile}
                      {a.tamaracModel ? ` · Tamarac ${a.tamaracModel}` : ''}{initial && manager !== 'Pure' ? ` · managed by ${manager}` : ''}
                      {(said.length > 0 || pairs.length > 0) && (
                        <div className="tt-muted tt-small">{[...said, ...pairs.map(p => `${p.label}: ${p.value}`)].join(' · ')}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </dd>
            {heldOut.length > 0 && (
              <>
                <dt>Set up first</dt>
                <dd>
                  <ul className="tt-plain">
                    {heldOut.map(a => {
                      const given = (draft.accountAnswers || {})[a.id] || {};
                      return <li key={a.id}>{a.name}: an Operations case to set it up with {chosenManager(a, given)} ({given.setupStrategy}). It is left out of this request until that is done.</li>;
                    })}
                  </ul>
                </dd>
              </>
            )}
            {questions.filter(f => ![undefined, false, ''].includes(answersToSend(draft.answers, draft.types, chosen)[f])).map(f => (
              <Fragment key={f}><dt>{FIELDS[f].label}</dt><dd>{showAnswer(f, draft.answers[f])}</dd></Fragment>
            ))}
            <dt>Additional instructions</dt>
            <dd className="tt-pre">{draft.answers.Description__c || <span className="tt-muted">{initial ? 'None: the answers for each account are the instruction.' : 'None'}</span>}</dd>
            {replacing && <><dt>Note on the replacement</dt><dd className="tt-pre">{draft.message || '(none)'}</dd></>}
          </dl>
          <div className="tt-actions">
            <button className="btn btn-primary" disabled={sending || Boolean(averages)} onClick={() => submit(false)}>
              {sending ? 'Sending…' : replacing ? 'Cancel the old request and send this one' : going.length ? 'Send to Investment Management' : 'Open the Operations cases'}
            </button>
            <button className="btn btn-light" disabled={sending} onClick={() => { setRefusal(null); setView('edit'); }}>Back to edit</button>
          </div>
        </section>
      </div>
    );
  }

  // The per-account section of an initial allocation, grouped by who manages each account.
  const groups = initial ? GROUP_ORDER.map(key => ({ key, list: chosen.filter(a => groupKeyFor(a, (draft.accountAnswers || {})[a.id]) === key) })).filter(g => g.list.length) : [];

  return (
    <div className="tt-page">
      <header className="tt-page-head">
        <div>
          <p className="tt-kicker">{replacing ? `Replacing ${replacing.name}, which Investment Management sent back` : 'New trade request'}</p>
          <h1>{household.name}</h1>
        </div>
        <button className="btn btn-light" onClick={() => navigate('/trade/requests')}>My requests</button>
      </header>

      {refusal && (
        <div className="tt-banner is-error" role="alert"><strong>Salesforce did not accept this request.</strong> {refusal.error}</div>
      )}

      <section className="tt-card">
        <h2>Household</h2>
        {!household.planOnFile && (
          <div className="tt-banner is-error">This household has no IPS, RTQ or suitability on file. Salesforce will refuse a trade request until one is recorded.</div>
        )}
        <dl className="tt-facts">
          <div><dt>PFA number</dt><dd>{household.pfaNumber || '–'}</dd></div>
          <div><dt>Owner</dt><dd>{household.owner}</dd></div>
          <div><dt>On file</dt><dd>{[household.hasIps && `IPS${household.ipsSignedDate ? ` (signed ${shortDate(household.ipsSignedDate)})` : ''}`, household.hasRtq && 'RTQ', household.suitability === 'Advisor Completed' && 'Suitability', household.ipsAmendment && 'IPS amendment'].filter(Boolean).join(', ') || 'Nothing'}</dd></div>
          <div><dt>Gains this year</dt><dd>{money(household.ytdLongTermGainLoss) || '$0'} long · {money(household.ytdShortTermGainLoss) || '$0'} short</dd></div>
        </dl>
        {household.tradeRequestNotes && <p className="tt-note"><strong>Trade notes:</strong> {household.tradeRequestNotes}</p>}
        {household.mostRecentTransfers && <p className="tt-note"><strong>Recent transfers:</strong> {household.mostRecentTransfers}</p>}
      </section>

      {openCases.length > 0 && (
        <section className={'tt-card' + (invalid.has('cases') ? ' is-invalid' : '')}>
          <h2>Open cases on this household</h2>
          <ul className="tt-list">
            {openCases.map(c => (
              <li key={c.id}>
                <SfLink base={salesforceUrl} id={c.id}><span className="tt-mono">{c.number}</span></SfLink> {c.subject}
                {c.type ? <span className="tt-muted"> · {c.type}</span> : c.recordType ? <span className="tt-muted"> · {c.recordType}</span> : null}
                {c.systematic ? <span className="tt-muted"> · systematic, {c.systematic.toLowerCase()}{c.amount ? ` ${money(c.amount)}` : ''}</span> : null}
                <span className="tt-tag">{c.status}</span>{c.owner ? <span className="tt-muted"> · {c.owner}</span> : null}
              </li>
            ))}
          </ul>
          <label className="tt-field is-check">
            <input type="checkbox" checked={draft.casesReviewed} onChange={e => update({ casesReviewed: e.target.checked })} />
            <span>I have reviewed these open cases</span>
          </label>
        </section>
      )}

      {others.length > 0 && (
        <section className="tt-card is-quiet">
          <h2>Requests already open for this household</h2>
          <ul className="tt-list">
            {others.map(r => (
              <li key={r.id}>
                <a href={`/trade/requests/${r.id}`} onClick={e => { e.preventDefault(); navigate(`/trade/requests/${r.id}`); }}>{r.name}</a>
                {' '}{typeLabels(r.types)} <span className="tt-tag">{r.status}</span> <span className="tt-muted">· {r.createdBy}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!replacing && situation !== 'allocated' && (
        <div className={'tt-banner ' + (initial ? 'is-ok' : 'is-warn')}>
          {initial ? (
            <>
              <strong>{situation === 'new' ? 'This household is new.' : 'New accounts in this household.'}</strong>{' '}
              {needing.length === 1 ? 'One account is waiting for its' : `${needing.length} accounts are waiting for their`} first allocation, so this is an initial allocation:
              answer for each account below, and Investment Management decides how to group and trade them.
              {!draft.override && (
                <div className="tt-banner-actions">
                  <button className="btn btn-light btn-sm" onClick={() => update({ override: true })}>Need a different request type?</button>
                </div>
              )}
            </>
          ) : (
            <>
              {needing.length === 1 ? 'One account here still needs its first allocation.' : `${needing.length} accounts here still need their first allocation.`}
              <div className="tt-banner-actions"><button className="btn btn-light btn-sm" onClick={() => chooseType(INITIAL)}>Do the initial allocation instead</button></div>
            </>
          )}
        </div>
      )}

      {showTypes && (
        <section className={'tt-card' + (invalid.has('types') ? ' is-invalid' : '')}>
          <h2>What is this request for?</h2>
          <div className="tt-chips" role="radiogroup" aria-label="Request type">
            {typeOptions.map(o => (
              <label key={o.value} className={'tt-chip is-large' + (draft.types.includes(o.value) ? ' is-on' : '')} title={o.hint}>
                <input type="radio" name="tt-request-type" checked={draft.types.includes(o.value)} onChange={() => chooseType(o.value)} />
                {o.label}
              </label>
            ))}
          </div>
          {typeHint && <p className="tt-help tt-type-hint">{typeHint}</p>}
          {situation === 'allocated' && !draft.override && (
            <p className="tt-help tt-type-hint">
              Every account here has been allocated, so an initial allocation is not offered.{' '}
              <button type="button" className="tt-linkish" onClick={() => update({ override: true })}>One of them still needs its first allocation?</button>
            </p>
          )}
        </section>
      )}

      <section className={'tt-card' + (invalid.has('accounts') ? ' is-invalid' : '')}>
        <h2>Accounts</h2>
        <p className="tt-muted">{initial
          ? 'Every account waiting for its first allocation is chosen. Investment Management sets the trading groups.'
          : 'A request covers one Tamarac group, or one account outside a group. Choosing an account in a group chooses the whole group, and choosing another group replaces it.'}</p>
        <table className="tt-table">
          <thead>
            <tr><th /><th>Account</th><th>Type</th><th>Tax</th><th className="num">Value</th><th className="num">Cash</th><th>Tamarac model</th><th>IPS · risk</th><th>Group</th></tr>
          </thead>
          <tbody>
            {accounts.map(a => {
              const blocker = accountBlocker(a, type);
              const on = selected.has(a.id);
              const state = allocationOf(a);
              const manager = managerOf(a);
              return (
                <tr key={a.id} className={(on ? 'is-on' : '') + (blocker ? ' is-blocked' : '')}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Include ${a.name}`}
                      checked={on}
                      disabled={Boolean(blocker) && !on}
                      onChange={() => update({ selected: [...toggleAccount(selected, a, accounts, !initial, !initial)] })}
                    />
                  </td>
                  <td>
                    <div className="tt-acct">{a.name}{manager !== 'Pure' && <span className="tt-badge">{manager}</span>}</div>
                    <div className="tt-muted tt-small">{a.client}{a.custodian ? ` · ${a.custodian}` : ''}{a.number ? ` · ${a.number}` : ''}</div>
                    {blocker && <div className="tt-flag is-error">{blocker}</div>}
                    {state === 'needs' && (
                      <div className="tt-flag is-warn">
                        Waiting for its first allocation{a.hadInitialAllocation ? ` · had one on ${shortDate(a.hadInitialAllocation)}, and Tamarac shows it waiting again` : ''}
                      </div>
                    )}
                    {state === 'inProgress' && <div className="tt-flag">Initial allocation in progress</div>}
                    {a.funded === false && <div className="tt-flag">Not funded yet</div>}
                    {a.excludedPositions.map(p => (
                      <div key={p.ticker} className="tt-flag">Excluded: {p.ticker}{p.name ? ` ${p.name}` : ''}{p.reason ? ` · ${p.reason}` : ''}{p.comments ? ` · ${p.comments}` : ''}</div>
                    ))}
                    {a.activeAverages.map(d => (
                      <div key={d.name} className="tt-flag is-warn">Active dollar-cost average {d.name} · {money(d.amount)}</div>
                    ))}
                    {a.gainsBudgetType && (
                      <div className="tt-flag">Gains budget {a.gainsBudgetType === 'Dollar' ? money(a.gainsBudget) : a.gainsBudgetType === 'Percent' ? `${a.gainsBudget}%` : 'no limit'}{a.gainsBudgetUsed != null ? ` · ${a.gainsBudgetUsed}% used` : ''}</div>
                    )}
                  </td>
                  <td>{a.type}</td>
                  <td>{taxLabel(a) || '–'}</td>
                  <td className="num">{money(a.value)}</td>
                  <td className="num">{money(a.cash)}{a.percentCash ? <div className="tt-muted tt-small">{Number(a.percentCash).toFixed(1)}%</div> : null}</td>
                  <td>{a.tamaracModel || <span className="tt-muted">none yet</span>}</td>
                  <td>{a.ipsModel || '–'} · {a.riskProfile || '–'}</td>
                  <td>{a.group || '–'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {initial && chosen.length > 0 && (
        <section className="tt-card">
          <h2>Each account</h2>
          <p className="tt-muted">Confirm who manages each account first: the questions follow from that. If Salesforce has an account on the wrong manager, say so here and Operations is asked to set it up.</p>
          {groups.map(g => (
            <div key={g.key} className="tt-account-group">
              <h3>{GROUP_TITLES[g.key]}</h3>
              <p className="tt-muted">{INTROS[g.key]}</p>
              {g.list.map(a => {
                const given = (draft.accountAnswers || {})[a.id] || {};
                const manager = chosenManager(a, given);
                const onFile = managerOf(a);
                const setUp = needsSetUp(a, given);
                const groupsFor = rowQuestionGroups(a, manager);
                const gainsDefault = defaultGainsBudget(a);
                const field = f => (
                  <AnswerField key={f} field={f} idSuffix={a.id} value={given[f]} onChange={(name, v) => setRowAnswer(a.id, name, v)} picklists={picklists}
                    {...(f === 'Gains_Budget_This_Year__c' ? { help: gainsDefault ? `Defaults to 2% of the account value, ${money(gainsDefault)}. Raise it to allow more gains; there is no separate carry-forward loss to enter.` : 'Defaults to 2% of the account value once it is funded.' } : {})} />
                );
                const choices = onFile === 'Other SMA' || onFile === 'Non-discretionary' ? [onFile] : MANAGERS;
                return (
                  <div key={a.id} className={'tt-account' + (invalid.has(`row:${a.id}`) ? ' is-invalid' : '')}>
                    <div className="tt-account-head">
                      <span className="tt-acct">{a.name}</span>
                      <span className="tt-muted"> · {a.type} · {money(a.value)}{a.funded === false ? ' · not funded yet' : ''} · IPS {a.ipsModel} · risk {a.riskProfile}{a.tamaracModel ? ` · Tamarac ${a.tamaracModel}` : ''}</span>
                    </div>
                    <div className="tt-manager">
                      <span className="tt-manager-label">Managed by</span>
                      <div className="tt-chips" role="radiogroup" aria-label={`Who manages ${a.name}`}>
                        {choices.map(m => (
                          <label key={m} className={'tt-chip' + (manager === m ? ' is-on' : '')}>
                            <input type="radio" name={`tt-manager-${a.id}`} checked={manager === m} disabled={choices.length === 1} onChange={() => setRowAnswer(a.id, 'Managed_By__c', m)} />
                            {m}{m === onFile ? <span className="tt-chip-note">on file</span> : null}
                          </label>
                        ))}
                      </div>
                    </div>
                    {setUp ? (
                      <div className="tt-banner is-warn">
                        <strong>Salesforce does not show this account on {manager} yet.</strong> Sending opens an Operations account maintenance case to set it up,
                        and the account is left out of this request until that is done; send its initial allocation then.
                        <div className="tt-grid tt-setup">
                          <div className="tt-field">
                            <label htmlFor={`tt-setup-strategy-${a.id}`}>{manager} strategy</label>
                            <input id={`tt-setup-strategy-${a.id}`} type="text" value={given.setupStrategy || ''} onChange={e => setRowAnswer(a.id, 'setupStrategy', e.target.value)} />
                            <p className="tt-help">{manager === 'Vise' ? 'As on the Vise proposal, for example Direct Index Core Equity.' : 'For example a corporate bond ladder, a CA muni ladder, or Core.'}</p>
                          </div>
                          <div className="tt-field">
                            <label htmlFor={`tt-setup-note-${a.id}`}>What Operations needs to know</label>
                            <textarea id={`tt-setup-note-${a.id}`} rows={2} value={given.setupNote || ''} onChange={e => setRowAnswer(a.id, 'setupNote', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <OnFile account={a} sfBase={salesforceUrl} />
                        <div className="tt-grid">{groupsFor.main.map(field)}</div>
                        <ManagerQuestions manager={manager} answers={given.manager || {}} onChange={(k, v) => setManagerAnswer(a.id, k, v)} idSuffix={a.id} />
                        {groupsFor.more.length > 0 && (
                          <details className="tt-more" open={groupsFor.more.some(f => given[f])}>
                            <summary>Anything else about this account</summary>
                            <div className="tt-grid">{groupsFor.more.map(field)}</div>
                          </details>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </section>
      )}

      {questions.length > 0 && (
        <section className="tt-card">
          <h2>{initial ? 'For the whole household' : 'About this request'}</h2>
          <div className="tt-grid">
            {questions.map(f => (f === 'Requested_Model__c'
              ? <ModelPicker key={f} value={draft.answers.Requested_Model__c} onChange={setAnswer} chosen={chosen} modifications={draft.answers.Model_Modifications__c} invalid={invalid.has(f)} />
              : <AnswerField key={f} field={f} value={draft.answers[f]} onChange={setAnswer} picklists={picklists} invalid={invalid.has(f)} />
            ))}
          </div>
        </section>
      )}

      <section className="tt-card">
        <h2>Anything else</h2>
        <div className="tt-grid">
          <AnswerField field="Description__c" value={draft.answers.Description__c} onChange={setAnswer} picklists={picklists} invalid={invalid.has('Description__c')}
            {...(initial ? { label: 'Additional instructions for Investment Management (optional)' } : {})} />
        </div>
        {chosen.some(isTaxable) && (
          <label className={'tt-field is-check' + (invalid.has('tax') ? ' is-invalid' : '')}>
            <input type="checkbox" checked={draft.consideredTax} onChange={e => update({ consideredTax: e.target.checked })} />
            <span>I have considered the tax consequences of this request (a taxable account is included)</span>
          </label>
        )}
        {replacing && (
          <div className="tt-field is-wide">
            <label htmlFor="tt-replace-note">Note to Investment Management about the replacement</label>
            <textarea id="tt-replace-note" rows={2} value={draft.message} onChange={e => update({ message: e.target.value })} />
          </div>
        )}
      </section>

      <div className="tt-footer">
        {showProblems && hard.length > 0 && (
          <ul className="tt-problems" role="alert">
            {hard.map((p, i) => <li key={i}>{p.message}</li>)}
          </ul>
        )}
        <button
          className="btn btn-primary"
          onClick={() => {
            if (hard.length) { setShowProblems(true); return; }
            setShowProblems(false);
            setRefusal(null);
            setView('review');
            window.scrollTo({ top: 0 });
          }}
        >
          Review before sending
        </button>
      </div>
    </div>
  );
}
